export interface AchOffsetInput {
    achCompany: {
        batchBalanceRequirements?: string;
        prefundingDays?: number;
        offsetAccountNumber?: string;
        allowUnbalancedPayments?: boolean;
        notOnUsTransactionTypes?: string;
    };
    creditAmount: number;
    debitAmount: number;
    offsetAccount?: unknown;
    /** FI-level override for allowUnbalancedPayments. Defaults to true when not provided. */
    fiAllowUnbalancedPayments?: boolean;
}

export interface AchOffsetState {
    visible: boolean;
    readOnly: boolean;
    required: boolean;
}

export class AchOffsetService {
    public static isPrefunding(hasEntitlement: boolean, input: AchOffsetInput): boolean {
        if (!hasEntitlement) {
            return false;
        }
        const prefundingDays = input.achCompany?.prefundingDays;
        if (!prefundingDays) {
            return false;
        }
        return prefundingDays > 0;
    }

    public static isAllowUnbalancedPayments(
        hasEntitlement: boolean,
        input: AchOffsetInput
    ): boolean {
        if (!hasEntitlement) {
            return false;
        }
        const fiAllowed = input.fiAllowUnbalancedPayments ?? true;
        return !!input.achCompany?.allowUnbalancedPayments && fiAllowed;
    }

    public static achCompanyIsBalanced(input: AchOffsetInput): boolean {
        const batchBalanceRequirements = input.achCompany?.batchBalanceRequirements;
        if (!batchBalanceRequirements) {
            return false;
        }
        return batchBalanceRequirements.toLowerCase().trim() === 'balanced';
    }

    public static fullOffsetAccountAllDebit(input: AchOffsetInput): boolean {
        const creditAmount = Number(input.creditAmount);
        const debitAmount = Number(input.debitAmount);
        const debitOnly = debitAmount > 0 && creditAmount === 0;
        const { notOnUsTransactionTypes, batchBalanceRequirements } = input.achCompany;
        return (
            notOnUsTransactionTypes === 'Debits and Credits' &&
            batchBalanceRequirements === 'Unbalanced - Full Offset' &&
            debitOnly
        );
    }

    public static requirePrefundingOffsetAccount(
        hasEntitlement: boolean,
        input: AchOffsetInput
    ): boolean {
        const prefunding = AchOffsetService.isPrefunding(hasEntitlement, input);
        const prefundedFullOffsetAllDebit =
            prefunding && AchOffsetService.fullOffsetAccountAllDebit(input);
        return !prefunding || prefundedFullOffsetAllDebit;
    }

    public static getOffsetAccountState(
        hasEntitlement: boolean,
        input: AchOffsetInput
    ): AchOffsetState {
        const balanced = AchOffsetService.achCompanyIsBalanced(input);
        const prefunding = AchOffsetService.isPrefunding(hasEntitlement, input);
        const fullOffsetAllDebit = AchOffsetService.fullOffsetAccountAllDebit(input);
        const allowUnbalanced = AchOffsetService.isAllowUnbalancedPayments(hasEntitlement, input);

        const visible = !balanced;
        const readOnly = (prefunding && !fullOffsetAllDebit) || allowUnbalanced || balanced;
        const hasExistingOffset = !!input.offsetAccount;
        const requirePrefunding = AchOffsetService.requirePrefundingOffsetAccount(
            hasEntitlement,
            input
        );
        const required = hasExistingOffset || (!balanced && !allowUnbalanced && requirePrefunding);

        return { visible, readOnly, required };
    }
}
